// Customer JWT authentication context for the marketplace embed.
//
// Stores the JWT in-memory (React state) as the authoritative source.
// sessionStorage is used as a transient cache so a page refresh within
// the same tab re-hydrates the token without forcing re-login. Lost on
// tab close — that is intentional (project rule #7: never localStorage
// as substitute for backend API).
//
// TD-366: the opaque refresh token is cached beside the JWT with the same
// per-tab semantics — a tab that stays open past the 15-minute access-JWT
// expiry silently renews (api-client's 401 path drives it); closing the tab
// still ends the session exactly as before.

import {
  createContext,
  useContext,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  setTokenProvider,
  setOnUnauthenticated,
  setRefreshProvider,
  API_BASE_URL,
} from "./api-client";

const SESSION_STORAGE_KEY = "aldelo_marketplace_jwt";
const REFRESH_STORAGE_KEY = "aldelo_marketplace_refresh_token";

interface AuthState {
  /** The JWT string, or null if unauthenticated. */
  token: string | null;
  /** The opaque single-use refresh token (TD-366), or null. */
  refreshToken: string | null;
  /** Whether the provider has finished hydrating from sessionStorage. */
  ready: boolean;
}

interface AuthContextValue extends AuthState {
  /**
   * Set tokens after successful login or silent renewal (TD-366). Writes the
   * sessionStorage cache. refreshToken is optional for backward compatibility
   * with pre-TD-366 callers and with an un-wired backend (legacy bare token).
   */
  setToken: (jwt: string, refreshToken?: string) => void;
  /** Clear token (logout or 401). Removes sessionStorage cache. */
  clearToken: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    // Hydrate from sessionStorage on mount (transient cache only).
    let cached: string | null = null;
    let cachedRefresh: string | null = null;
    try {
      cached = sessionStorage.getItem(SESSION_STORAGE_KEY);
      cachedRefresh = sessionStorage.getItem(REFRESH_STORAGE_KEY);
    } catch {
      // sessionStorage may be unavailable in sandboxed iframes.
    }
    return { token: cached, refreshToken: cachedRefresh, ready: true };
  });

  // Refs mirror state.token/state.refreshToken but update SYNCHRONOUSLY.
  // The api-client's 401-renewal path retries within the same microtask
  // continuation — React has not re-rendered yet, so a provider that closes
  // over `state.token` hands the retry the just-expired JWT (and a rapid
  // second renewal would re-send an already-rotated refresh token). Found by
  // auth-refresh.test.tsx's "session survives" case: the retried request
  // went out with the STALE Bearer.
  const tokenRef = useRef<string | null>(state.token);
  const refreshTokenRef = useRef<string | null>(state.refreshToken);

  const setToken = useCallback((jwt: string, refreshToken?: string) => {
    const rt = refreshToken ?? "";
    tokenRef.current = jwt;
    refreshTokenRef.current = rt || null;
    setState({ token: jwt, refreshToken: rt || null, ready: true });
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, jwt);
      if (rt) {
        sessionStorage.setItem(REFRESH_STORAGE_KEY, rt);
      } else {
        sessionStorage.removeItem(REFRESH_STORAGE_KEY);
      }
    } catch {
      // Best-effort cache.
    }
  }, []);

  const clearToken = useCallback(() => {
    tokenRef.current = null;
    refreshTokenRef.current = null;
    setState({ token: null, refreshToken: null, ready: true });
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      sessionStorage.removeItem(REFRESH_STORAGE_KEY);
    } catch {
      // Best-effort.
    }
  }, []);

  // Register the token provider, silent-renewal provider (TD-366) and 401
  // handler with the API client.
  //
  // useLayoutEffect, NOT useEffect — this is a cross-provider ordering
  // contract, not a preference. `token` is hydrated synchronously in the
  // useState initializer above, so a child that reads it fires its own mount
  // effect with a live token; but React flushes PASSIVE effects child-first,
  // so a passive registration here always loses the race to every consumer
  // below it. CartProvider (AuthProvider's child in MarketplaceEmbed.tsx) hit
  // exactly that: its mount effect ran replayGuestCart() — which claims and
  // DELETES the guest buffer up front to stay replay-at-most-once — and POSTed
  // each staged line before `tokenProvider` existed, so the request went out
  // with no Authorization header, 401'd, and destroyed the customer's cart
  // with nothing committed server-side (verified live: the replay POST had no
  // `authorization` header while the later GET did).
  //
  // Layout effects flush for the WHOLE tree before ANY passive effect runs, so
  // this registration is guaranteed to be in place before any child's mount
  // effect issues a request. Pinned by auth-cart-token-seam.test.tsx, which
  // mocks only global fetch so the real embedApiFetch reads the real
  // registration state. Safe here: the embed is client-only (mounted via
  // querySelectorAll in index.tsx), so there is no SSR pass to warn about.
  //
  // The refresh provider deliberately uses a PLAIN fetch (not embedApiFetch):
  // the renewal call itself must never recurse into the 401-retry path, and
  // it carries no Bearer header — the opaque token in the body is the
  // credential. On failure it returns null and lets the caller's
  // onUnauthenticated listener clear state (single responsibility).
  useLayoutEffect(() => {
    setTokenProvider(() => tokenRef.current);
    setOnUnauthenticated(() => {
      clearToken();
    });
    setRefreshProvider(async () => {
      const rt = refreshTokenRef.current;
      if (!rt) return null;
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/marketplace/auth/refresh`, {
          method: "POST",
          credentials: "omit",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ refresh_token: rt }),
        });
        if (!res.ok) return null;
        const payload = (await res.json().catch(() => null)) as {
          data?: { token?: string; refresh_token?: string };
        } | null;
        const token = payload?.data?.token;
        if (!token) return null;
        // Persist the ROTATED pair — the old refresh token is single-use and
        // already consumed server-side. setToken also updates the refs, so a
        // retrying request picks the fresh JWT up synchronously.
        setToken(token, payload?.data?.refresh_token ?? "");
        return token;
      } catch {
        return null;
      }
    });
    return () => {
      setTokenProvider(null);
      setOnUnauthenticated(null);
      setRefreshProvider(null);
    };
  }, [clearToken, setToken]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, setToken, clearToken }),
    [state, setToken, clearToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hook to access customer auth state. Must be used inside AuthProvider. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
